import type { Action, Resource } from "@/types/permissions";
import { UserRole, type UserRole as UserRoleType } from "@/types/user";

// Complete permissions matrix based on PRD v3.0
export const RESOURCES = {
  USERS: "users",
  CLIENTS: "clients",
  PROJECTS: "projects",
  TASKS: "tasks",
  MILESTONES: "milestones",
  DAILY_LOGS: "daily_logs",
  PAYABLE_INVOICES: "payable_invoices",
  CLIENT_INVOICES: "client_invoices",
  CHANGE_ORDERS: "change_orders",
  MATERIAL_DECISIONS: "material_decisions",
  PUNCHLIST_ITEMS: "punchlist_items",
  DOCUMENTS: "documents",
  PROJECT_PHOTOS: "project_photos",
  CONTRACTORS: "contractors",
  NOTIFICATIONS: "notifications",
  AUDIT_LOGS: "audit_logs",
  BUDGET_CATEGORIES: "budget_categories",
  PURCHASE_ORDERS: "purchase_orders",
  SUPPLIERS: "suppliers",
  PAYMENT_REPORTS: "payment_reports",
} as const;

export const ACTIONS = {
  CREATE: "create",
  READ: "read",
  UPDATE: "update",
  DELETE: "delete",
  APPROVE: "approve",
  REJECT: "reject",
  SUBMIT: "submit",
  PAY: "pay",
  SIGN: "sign",
  UPLOAD: "upload",
} as const;

export const ROLE_PERMISSIONS: Record<
  UserRoleType,
  { resource: Resource; actions: Action[] }[]
> = {
  [UserRole.SUPERADMIN]: [
    {
      resource: RESOURCES.USERS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.CLIENTS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.PROJECTS,
      actions: [
        ACTIONS.CREATE,
        ACTIONS.READ,
        ACTIONS.UPDATE,
        ACTIONS.DELETE,
        ACTIONS.APPROVE,
      ],
    },
    {
      resource: RESOURCES.TASKS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.MILESTONES,
      actions: [
        ACTIONS.CREATE,
        ACTIONS.READ,
        ACTIONS.UPDATE,
        ACTIONS.APPROVE,
        ACTIONS.REJECT,
        ACTIONS.SUBMIT,
      ],
    },
    {
      resource: RESOURCES.DAILY_LOGS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.PAYABLE_INVOICES,
      actions: [
        ACTIONS.CREATE,
        ACTIONS.READ,
        ACTIONS.UPDATE,
        ACTIONS.APPROVE,
        ACTIONS.REJECT,
        ACTIONS.PAY,
      ],
    },
    {
      resource: RESOURCES.CLIENT_INVOICES,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.CHANGE_ORDERS,
      actions: [
        ACTIONS.CREATE,
        ACTIONS.READ,
        ACTIONS.UPDATE,
        ACTIONS.SIGN,
        ACTIONS.APPROVE,
        ACTIONS.REJECT,
      ],
    },
    {
      resource: RESOURCES.MATERIAL_DECISIONS,
      actions: [
        ACTIONS.CREATE,
        ACTIONS.READ,
        ACTIONS.UPDATE,
        ACTIONS.DELETE,
        ACTIONS.APPROVE,
        ACTIONS.SUBMIT,
      ],
    },
    {
      resource: RESOURCES.PUNCHLIST_ITEMS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.DOCUMENTS,
      actions: [
        ACTIONS.CREATE,
        ACTIONS.READ,
        ACTIONS.UPDATE,
        ACTIONS.DELETE,
        ACTIONS.UPLOAD,
      ],
    },
    {
      resource: RESOURCES.PROJECT_PHOTOS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.DELETE, ACTIONS.UPLOAD],
    },
    {
      resource: RESOURCES.CONTRACTORS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.NOTIFICATIONS,
      actions: [ACTIONS.READ, ACTIONS.UPDATE],
    },
    { resource: RESOURCES.AUDIT_LOGS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.BUDGET_CATEGORIES,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.PURCHASE_ORDERS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.SUPPLIERS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.PAYMENT_REPORTS,
      actions: [
        ACTIONS.CREATE,
        ACTIONS.READ,
        ACTIONS.UPDATE,
        ACTIONS.DELETE,
        ACTIONS.APPROVE,
        ACTIONS.REJECT,
      ],
    },
  ],
  [UserRole.PM]: [
    { resource: RESOURCES.USERS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.CLIENTS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE],
    },
    {
      resource: RESOURCES.PROJECTS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.APPROVE],
    },
    {
      resource: RESOURCES.TASKS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.MILESTONES,
      actions: [
        ACTIONS.CREATE,
        ACTIONS.READ,
        ACTIONS.UPDATE,
        ACTIONS.APPROVE,
        ACTIONS.REJECT,
      ],
    },
    { resource: RESOURCES.DAILY_LOGS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.PAYABLE_INVOICES,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.APPROVE, ACTIONS.REJECT],
    },
    { resource: RESOURCES.CLIENT_INVOICES, actions: [ACTIONS.READ] },
    { resource: RESOURCES.PAYMENT_REPORTS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.CHANGE_ORDERS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE],
    },
    // CHANGED: added DELETE — PM can soft-delete a decision pre-selection (blocked once status = selected)
    {
      resource: RESOURCES.MATERIAL_DECISIONS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.PUNCHLIST_ITEMS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.DOCUMENTS,
      actions: [
        ACTIONS.CREATE,
        ACTIONS.READ,
        ACTIONS.UPDATE,
        ACTIONS.DELETE,
        ACTIONS.UPLOAD,
      ],
    },
    { resource: RESOURCES.PROJECT_PHOTOS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.CONTRACTORS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE],
    },
    {
      resource: RESOURCES.NOTIFICATIONS,
      actions: [ACTIONS.READ, ACTIONS.UPDATE],
    },
    { resource: RESOURCES.AUDIT_LOGS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.BUDGET_CATEGORIES,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE],
    },
    {
      resource: RESOURCES.PURCHASE_ORDERS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE],
    },
    {
      resource: RESOURCES.SUPPLIERS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE],
    },
  ],
  [UserRole.SE]: [
    { resource: RESOURCES.USERS, actions: [ACTIONS.READ] },
    { resource: RESOURCES.CLIENTS, actions: [ACTIONS.READ] },
    { resource: RESOURCES.PROJECTS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.TASKS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE],
    },
    {
      resource: RESOURCES.MILESTONES,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.SUBMIT],
    },
    {
      resource: RESOURCES.DAILY_LOGS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE],
    },
    {
      resource: RESOURCES.PAYABLE_INVOICES,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.APPROVE, ACTIONS.REJECT],
    },
    { resource: RESOURCES.CLIENT_INVOICES, actions: [ACTIONS.READ] },
    { resource: RESOURCES.CHANGE_ORDERS, actions: [ACTIONS.READ] },
    { resource: RESOURCES.PUNCHLIST_ITEMS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.DOCUMENTS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPLOAD],
    },
    {
      resource: RESOURCES.PROJECT_PHOTOS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPLOAD],
    },
    { resource: RESOURCES.CONTRACTORS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.NOTIFICATIONS,
      actions: [ACTIONS.READ, ACTIONS.UPDATE],
    },
    { resource: RESOURCES.BUDGET_CATEGORIES, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.PURCHASE_ORDERS,
      actions: [ACTIONS.READ, ACTIONS.UPDATE],
    },
    // NOTE: SE intentionally has no MATERIAL_DECISIONS entry — SE is not part of
    // the sourcing/upload workflow per the design discussed (PM/Finance only).
    // SE gets notified when a PO is marked Delivered via NOTIFICATIONS, not via
    // direct access to material_decisions.
  ],
  [UserRole.ENGINEER]: [
    { resource: RESOURCES.USERS, actions: [ACTIONS.READ] },
    { resource: RESOURCES.CLIENTS, actions: [] },
    { resource: RESOURCES.PROJECTS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.MILESTONES,
      actions: [ACTIONS.READ, ACTIONS.APPROVE, ACTIONS.REJECT],
    },
    { resource: RESOURCES.PROJECT_PHOTOS, actions: [ACTIONS.READ] },
  ],
  [UserRole.FINANCE]: [
    { resource: RESOURCES.USERS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.CLIENTS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE],
    },
    { resource: RESOURCES.PROJECTS, actions: [ACTIONS.READ] },
    { resource: RESOURCES.TASKS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.MILESTONES,
      actions: [ACTIONS.READ],
    },
    {
      resource: RESOURCES.PAYABLE_INVOICES,
      actions: [
        ACTIONS.CREATE,
        ACTIONS.READ,
        ACTIONS.APPROVE,
        ACTIONS.REJECT,
        ACTIONS.PAY,
      ],
    },
    {
      resource: RESOURCES.CLIENT_INVOICES,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE],
    },
    {
      resource: RESOURCES.PAYMENT_REPORTS,
      actions: [ACTIONS.READ, ACTIONS.APPROVE, ACTIONS.REJECT],
    },
    { resource: RESOURCES.CHANGE_ORDERS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.MATERIAL_DECISIONS,
      actions: [ACTIONS.READ, ACTIONS.UPDATE],
    },
    {
      resource: RESOURCES.NOTIFICATIONS,
      actions: [ACTIONS.READ, ACTIONS.UPDATE],
    },
    { resource: RESOURCES.DOCUMENTS, actions: [ACTIONS.READ] },
    { resource: RESOURCES.BUDGET_CATEGORIES, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.PURCHASE_ORDERS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE],
    },
    {
      resource: RESOURCES.CONTRACTORS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    {
      resource: RESOURCES.SUPPLIERS,
      actions: [ACTIONS.CREATE, ACTIONS.READ, ACTIONS.UPDATE, ACTIONS.DELETE],
    },
    { resource: RESOURCES.AUDIT_LOGS, actions: [ACTIONS.READ] },
  ],
  [UserRole.CLIENT_VIEWER]: [
    { resource: RESOURCES.PROJECTS, actions: [ACTIONS.READ] },
    { resource: RESOURCES.MILESTONES, actions: [ACTIONS.READ] },
    { resource: RESOURCES.DAILY_LOGS, actions: [ACTIONS.READ] },
    { resource: RESOURCES.CLIENT_INVOICES, actions: [ACTIONS.READ] },
    { resource: RESOURCES.CHANGE_ORDERS, actions: [ACTIONS.READ] },
    // CHANGED: added — client_viewer can see the Materials tab (pending +
    // decided items) read-only, but cannot respond to sourcing prompts or
    // make selections (no SUBMIT/APPROVE) — matches "viewer sees no actionable
    // cards" design decision.
    { resource: RESOURCES.MATERIAL_DECISIONS, actions: [ACTIONS.READ] },
    { resource: RESOURCES.PROJECT_PHOTOS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.NOTIFICATIONS,
      actions: [ACTIONS.READ, ACTIONS.UPDATE],
    },
  ],
  [UserRole.CLIENT_DECISION_MAKER]: [
    { resource: RESOURCES.PROJECTS, actions: [ACTIONS.READ] },
    { resource: RESOURCES.MILESTONES, actions: [ACTIONS.READ] },
    { resource: RESOURCES.DAILY_LOGS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.CLIENT_INVOICES,
      actions: [ACTIONS.READ, ACTIONS.SUBMIT],
    },
    {
      resource: RESOURCES.PAYMENT_REPORTS,
      actions: [ACTIONS.CREATE, ACTIONS.READ],
    },
    {
      resource: RESOURCES.CHANGE_ORDERS,
      actions: [ACTIONS.READ, ACTIONS.SIGN, ACTIONS.APPROVE, ACTIONS.REJECT],
    },
    // CHANGED: split single APPROVE into two distinct actions matching the two
    // client-triggered endpoints:
    //   SUBMIT  -> POST .../sourcing-preference (self_supply or request_sourcing)
    //   APPROVE -> POST .../select (final option pick — permanent/locking)
    {
      resource: RESOURCES.MATERIAL_DECISIONS,
      actions: [ACTIONS.READ, ACTIONS.SUBMIT, ACTIONS.APPROVE],
    },
    {
      resource: RESOURCES.PUNCHLIST_ITEMS,
      actions: [ACTIONS.CREATE, ACTIONS.READ],
    },
    { resource: RESOURCES.PROJECT_PHOTOS, actions: [ACTIONS.READ] },
    {
      resource: RESOURCES.NOTIFICATIONS,
      actions: [ACTIONS.READ, ACTIONS.UPDATE],
    },
  ],
};
